import { Subject, distinctUntilChanged, map, takeUntil, debounceTime } from 'rxjs';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ContactService } from 'src/app/services/contact.service';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { CaseModel } from 'src/app/models/case.model';
import { ContactModel } from 'src/app/models/contact.model';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { last } from 'lodash';

const alphaNumericRegex = /^[A-Za-z0-9]{1}/;

enum ScrollDirection {
  UP,
  DOWN,
}

@Component({
  selector: 'tx-client-case-tree',
  templateUrl: './client-case-tree.component.html',
  styleUrls: ['./client-case-tree.component.scss'],
})
export class ClientCaseTreeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() preselectedContactId: string = '';
  @Input() preselectedCaseId: string = '';
  @Output() caseSelected: EventEmitter<CaseModel> = new EventEmitter();
  @ViewChild('filterInput') filterInput!: ElementRef;
  @ViewChild('caseSelector') caseSelector!: NgbDropdown;
  @ViewChild('itemsList') itemsList!: ElementRef;
  private contactsAndCases: ContactModel[] = [];
  filteredContactsAndCases: ContactModel[] = [];
  selectedCase: CaseModel | null = null;
  private selectedContact: ContactModel | null = null;
  showFilter = true;
  label: string | null = null;
  searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  loading = false;
  filterText = '';

  constructor(private contactService: ContactService, private documentDataService: DocumentDataService) {}

  ngOnInit() {
    this.fetchClientsAndCases();
    this.initFiltering();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Open dropdown by default if nothing is selected yet
    if (!this.preselectedContactId && !this.preselectedCaseId) {
      this.caseSelector.open();
    }
  }

  initFiltering() {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroy$), // Unsubscribe when the component is destroyed
        debounceTime(250), // wait 250ms after each keystroke before considering the term
        map((term) => term.trim()), // Remove whitespace from begin or end
        distinctUntilChanged() // ignore new term if same as previous term
      )
      .subscribe({
        next: (searchTerm) => {
          this.filteredContactsAndCases =
            searchTerm.length > 1 ? this.filterParentsAndChildren(searchTerm) : this.contactsAndCases;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  filterParentsAndChildren(searchTerm: string): ContactModel[] {
    searchTerm = searchTerm.toLowerCase(); // Convert the search term to lowercase for case-insensitive matching

    return this.contactsAndCases.reduce((result, current) => {
      let contact = current;
      const isContactMatch = `${contact.firstName} ${contact.lastName}`.toLowerCase().indexOf(searchTerm) > -1;

      if (!isContactMatch) {
        // Check if any child matches the given searchTerm
        const filteredCases = contact.cases.filter((cas) => cas.description.toLowerCase().indexOf(searchTerm) > -1);

        if (filteredCases.length > 0) {
          contact = {
            ...contact,
            cases: filteredCases,
          };
        } else {
          //  Don't include contact if we don't have any case match
          return result;
        }
      }

      return [...result, contact];
    }, [] as ContactModel[]);
  }

  fetchClientsAndCases() {
    this.loading = true;
    this.contactService.getContactsAndCases().subscribe({
      next: (res) => {
        if (res && res.contacts) {
          this.contactsAndCases = this.filteredContactsAndCases = res.contacts as ContactModel[];

          this.handlePreSelectedValues();
          this.loading = false;
        }
      },
      error: (err) => (this.loading = false),
    });
  }

  private handlePreSelectedValues() {
    const selectedContact = this.preselectedContactId
      ? this.contactsAndCases?.find((c) => c.ctcID === this.preselectedContactId) || null
      : null;

    const selectedCase =
      selectedContact && this.preselectedCaseId
        ? selectedContact?.cases.find((c) => c.casID === this.preselectedCaseId) || null
        : null;

    this.applySelectedClientAndCaseFilter(selectedContact, selectedCase);
  }

  applySelectedClientAndCaseFilter(contact: ContactModel | null, cas: CaseModel | null) {
    if (contact) {
      this.showFilter = false;
      this.selectedContact = contact;
      this.filteredContactsAndCases = [this.selectedContact];
      if (contact.cases.length <= 1) {
        this.label = null;
        this.showFilter = true;
        this.clearFilter();
        this.focusFilterInput();
      }
    }

    if (cas) {
      this.selectedCase = cas;
      this.setLabel();
    }

    if (contact && cas) {
      cas.ctcFirstName = contact.firstName;
      cas.ctcLastName = contact.lastName;
      cas.ctcId = contact.ctcID;
      this.caseSelected.emit(cas);
    }
  }

  clearFilter() {
    this.filteredContactsAndCases = this.contactsAndCases;
    this.filterText = '';
    this.focusFilterInput();
  }

  private focusFilterInput() {
    if (this.filterInput) {
      setTimeout(() => {
        this.filterInput?.nativeElement.focus();
      }, 0);
    }
  }

  private setLabel() {
    const selectedCase = this.selectedCase ? ` - ${this.selectedCase.description}` : '';
    this.label = `${this.selectedContact?.firstName} ${this.selectedContact?.lastName} ${selectedCase}`;
  }

  showAllClients() {
    this.label = null;
    this.showFilter = true;
    this.clearFilter();
    this.focusFilterInput();
    if (this.selectedCase?.casID) {
      setTimeout(() => {
        const selected = document.getElementById(`case-${this.selectedCase?.casID}`);
        if (selected) {
          selected.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
    return false;
  }

  onCaseClicked(contact: ContactModel, cas: CaseModel, event: MouseEvent | null) {
    if (event) {
      event.stopPropagation();
    }
    this.documentDataService.setCasId(cas.casID);
    this.documentDataService.setCtcId(contact.ctcID);
    this.preselectedCaseId = cas.casID;
    this.preselectedContactId = contact.ctcID;
    cas.ctcFirstName = contact.firstName;
    cas.ctcLastName = contact.lastName;
    this.showFilter = false;
    this.applySelectedClientAndCaseFilter(contact, cas);
    // this.caseSelected.emit(cas);
    return false;
  }

  onFilterChange() {
    this.searchTerm$.next(this.filterText);
  }

  onDropdownToggle(isOpen: boolean) {
    if (isOpen) {
      this.focusFilterInput();
    }
    // set timeout to wait for the DOM to be updated
    if (this.selectedCase?.casID && isOpen) {
      setTimeout(() => {
        const selected = document.getElementById(`case-${this.selectedCase?.casID}`);
        if (selected) {
          selected.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }

  toggleContact(contact: ContactModel) {
    contact.expanded = !contact.expanded;
    return false;
  }

  onKeyDownFilterInput(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        const firstContactID = this.filteredContactsAndCases[0].ctcID;
        this.applyfocusOnElement(`contact-${firstContactID}`);
        return false;
    }
    return true;
  }

  onKeyDownContact(event: KeyboardEvent, contact: ContactModel, index: number) {
    const key = event.shiftKey ? `Shift${event.key}` : event.key;

    switch (key) {
      case 'Enter':
      case 'ArrowRight':
        this.toggleContact(contact);
        break;
      case 'Tab':
      case 'ArrowDown':
        const id = contact.expanded
          ? `case-${contact.cases[0].casID}`
          : `contact-${this.filteredContactsAndCases[index + 1].ctcID}`;
        this.applyfocusOnElement(id);
        break;
      case 'ArrowUp':
      case 'ShiftTab':
        if (index > 0) {
          const previousContact = this.filteredContactsAndCases[index - 1];
          const id = previousContact?.expanded
            ? `case-${last(previousContact.cases)?.casID}`
            : `contact-${previousContact.ctcID}`;
          this.applyfocusOnElement(id);
        } else {
          this.focusFilterInput();
        }
        break;
      case 'PageUp':
        this.handlePageUpOrDown(ScrollDirection.UP);
        break;
      case 'PageDown':
        this.handlePageUpOrDown(ScrollDirection.DOWN);
        break;
      default:
        this.onEnterAlphaNumericChar(key);
    }
    return false;
  }

  onKeyDownCase(event: KeyboardEvent, contact: ContactModel, contactIndex: number, cas: CaseModel, caseIndex: number) {
    const key = event.shiftKey ? `Shift${event.key}` : event.key;

    switch (key) {
      case 'Tab':
      case 'ArrowDown':
        const nextId =
          caseIndex < contact.cases.length - 1
            ? `case-${contact.cases[caseIndex + 1].casID}`
            : `contact-${this.filteredContactsAndCases[contactIndex + 1].ctcID}`;
        this.applyfocusOnElement(nextId);
        break;
      case 'ArrowUp':
      case 'ShiftTab':
        const prevId = caseIndex > 0 ? `case-${contact.cases[caseIndex - 1]?.casID}` : `contact-${contact.ctcID}`;
        this.applyfocusOnElement(prevId);
        break;
      case 'Enter':
        this.onCaseClicked(contact, cas, null);
        this.caseSelector.close();
        break;
      case 'PageUp':
        this.handlePageUpOrDown(ScrollDirection.UP);
        break;
      case 'PageDown':
        this.handlePageUpOrDown(ScrollDirection.DOWN);
        break;
      default:
        this.onEnterAlphaNumericChar(key);
    }

    return false;
  }

  applyfocusOnElement(elementId: string) {
    document.getElementById(elementId)?.focus();
  }

  onEnterAlphaNumericChar(key: string) {
    // append char to filter input if it is alphanumeric
    if (key.length === 1 && alphaNumericRegex.test(key)) {
      this.filterText += key;
      this.onFilterChange();
      this.focusFilterInput();
    }
  }

  handlePageUpOrDown(direction: ScrollDirection) {
    const itemsList: HTMLElement = this.itemsList.nativeElement;
    // get dropdown height with a smoll offset to actually select the next item just outside the container
    const dropdownHeight = itemsList.clientHeight - 20;
    const scrollAmount = direction === ScrollDirection.UP ? -dropdownHeight : dropdownHeight;

    // scroll up or down
    itemsList.scrollBy({
      top: scrollAmount,
      behavior: 'smooth',
    });

    // find the first fully visible element
    const visibleContact = this.findFirstFullyVisibleContactElement(itemsList);

    // focus the element
    if (visibleContact) {
      this.applyfocusOnElement(`contact-${visibleContact.ctcID}`);
    }
  }

  findFirstFullyVisibleContactElement(itemsList: HTMLElement): ContactModel | undefined {
    const scrollTop = itemsList.scrollTop;
    const visibleHeight = itemsList.clientHeight;

    return this.filteredContactsAndCases.find((contact) => {
      const element = document.getElementById(`contact-${contact.ctcID}`);
      if (!element) return false;

      const itemTop = element.offsetTop - itemsList.offsetTop;
      const itemBottom = itemTop + element.clientHeight;
      return itemTop >= scrollTop && itemBottom <= scrollTop + visibleHeight;
    });
  }
}
